import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--violet)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?3:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };
const sig = x => 1/(1+Math.exp(-Math.max(-20,Math.min(20,x))));
const tanh_ = x => Math.tanh(Math.max(-20,Math.min(20,x)));
const dot = (w,v) => w.reduce((s,wi,i)=>s+wi*v[i],0);

const mkW = n => Array.from({length:n},()=>randn()*0.5);

// Vanilla RNN step
const rnnStep = (x, h_prev, Wh, Wx, b) => {
  const z = dot(Wh,h_prev) + dot(Wx,x) + b;
  const h = tanh_(z);
  return {h, z, gates:{h}};
};

// LSTM step (scalar, 1 unit for visualization)
const lstmStep = (x, h_prev, c_prev, Wf,Wi,Wc,Wo, bf,bi,bc,bo) => {
  const concat = [...(Array.isArray(x)?x:[x]), h_prev];
  const f  = sig(dot(Wf,concat)+bf);
  const i_ = sig(dot(Wi,concat)+bi);
  const ct = tanh_(dot(Wc,concat)+bc);
  const o  = sig(dot(Wo,concat)+bo);
  const c  = f*c_prev + i_*ct;
  const h  = o*tanh_(c);
  return {h,c,gates:{f,i:i_,c_tilde:ct,o},forget:f,input:i_,cell:c};
};

// GRU step (scalar, 1 unit)
const gruStep = (x, h_prev, Wr,Wz,Wn, br,bz,bn) => {
  const xv = Array.isArray(x)?x:[x];
  const concat = [...xv, h_prev];
  const r  = sig(dot(Wr,concat)+br);
  const z  = sig(dot(Wz,concat)+bz);
  const n  = tanh_(dot(Wn,[...xv, r*h_prev])+bn);
  const h  = (1-z)*n + z*h_prev;
  return {h,gates:{r,z,n}};
};

const TABS = ['unrolled','gates','cell-state','vanishing','compare'];

// ─── UNROLLED RNN SVG ─────────────────────────────────────────────────────────
function UnrolledRNN({steps, hiddenVals, type='lstm', gates=null}) {
  const VW=700, VH=200, T=steps.length;
  const xGap=(VW-80)/(T+1);
  const cy=VH/2, R=22;

  const gateColor = (v, gate) => {
    if(gate==='f') return `rgba(248,113,113,${0.1+v*0.9})`;
    if(gate==='i') return `rgba(34,211,238,${0.1+v*0.9})`;
    if(gate==='o') return `rgba(74,222,128,${0.1+v*0.9})`;
    return `rgba(167,139,250,${0.1+Math.abs(v)*0.9})`;
  };

  return (
    <svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Hidden state connections */}
      {hiddenVals.slice(0,-1).map((_,t)=>{
        const x1=60+t*xGap+R, y1=cy, x2=60+(t+1)*xGap-R, y2=cy;
        const hv=Math.abs(hiddenVals[t]);
        return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke={`rgba(167,139,250,${0.2+hv*0.8})`} strokeWidth={1+hv*3} markerEnd="url(#arrow)"/>;
      })}
      {/* Arrow marker */}
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#a78bfa" opacity="0.7"/>
        </marker>
      </defs>
      {/* Cells */}
      {steps.map((x,t)=>{
        const cx=60+t*xGap; const hv=hiddenVals[t]??0;
        const fill=gateColor(Math.abs(hv),'h');
        const g=gates?.[t];
        return (
          <g key={t}>
            {/* Input x(t) */}
            <line x1={cx} y1={VH-30} x2={cx} y2={cy+R} stroke="#64748b" strokeWidth={1} strokeDasharray="3 2"/>
            <text x={cx} y={VH-16} textAnchor="middle" style={{fontSize:'9px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>x{t}</text>
            <text x={cx} y={VH-6} textAnchor="middle" style={{fontSize:'8px',fill:'#94a3b8',fontFamily:'JetBrains Mono'}}>{typeof x==='number'?x.toFixed(2):x}</text>
            {/* Cell body */}
            <circle cx={cx} cy={cy} r={R} fill={fill} stroke={'#334155'} strokeWidth={1}/>
            {/* Gate indicators (tiny colored dots inside) */}
            {g&&type==='lstm'&&[
              {key:'f',dx:-8,dy:-6,c:'#f87171'},
              {key:'i',dx:8,dy:-6,c:'#22d3ee'},
              {key:'o',dx:0,dy:8,c:'#4ade80'},
            ].map(({key,dx,dy,c})=>(
              <circle key={key} cx={cx+dx} cy={cy+dy} r={4} fill={`rgba(${c==='#f87171'?'248,113,113':c==='#22d3ee'?'34,211,238':'74,222,128'},${0.1+(g[key]??0)*0.9})`} stroke={c} strokeWidth={0.5}/>
            ))}
            {/* h(t) value */}
            <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'9px',fill:'#f1f5f9',fontFamily:'JetBrains Mono',fontWeight:600,pointerEvents:'none'}}>{hv.toFixed(2)}</text>
            {/* Output arrow */}
            <line x1={cx} y1={cy-R} x2={cx} y2={20} stroke={`rgba(74,222,128,${0.2+Math.abs(hv)*0.8})`} strokeWidth={1} strokeDasharray="3 2"/>
            <text x={cx} y={14} textAnchor="middle" style={{fontSize:'8px',fill:'#4ade80',fontFamily:'JetBrains Mono'}}>h{t}</text>
          </g>
        );
      })}
      {/* Type label */}
      <text x={8} y={cy} textAnchor="start" dominantBaseline="middle" style={{fontSize:'10px',fill:'#a78bfa',fontFamily:'Syne',fontWeight:800}}>{type.toUpperCase()}</text>
    </svg>
  );
}

// ─── GATE HEATMAP ─────────────────────────────────────────────────────────────
function GateBar({val, label, color}) {
  const pct=Math.max(0,Math.min(100,val*100));
  return (
    <div style={{marginBottom:'5px'}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'2px',fontSize:'9px'}}>
        <span style={{color:'var(--t3)'}}>{label}</span>
        <span style={{color,fontFamily:'JetBrains Mono',fontWeight:700}}>{val.toFixed(3)}</span>
      </div>
      <div style={{height:8,background:'var(--b0)',borderRadius:4,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:`linear-gradient(90deg,${color}44,${color})`,borderRadius:4,transition:'width .2s'}}/>
      </div>
    </div>
  );
}

export default function RNNLab() {
  const [tab,setTab]=useState('unrolled');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'RNN Lab — run a sequence to see gates activate at each timestep.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [seqLen,setSeqLen]=useState(8);
  const [hidSize,setHidSize]=useState(1);
  const [modelType,setModelType]=useState('lstm');
  const [inputSeq,setInputSeq]=useState('sin');
  const [running,setRunning]=useState(false);

  const [result,setResult]=useState(null);
  const [selectedT,setSelectedT]=useState(0);

  // Generate input sequence
  const genSeq = (type,n) => {
    if(type==='sin')    return Array.from({length:n},(_,i)=>+(Math.sin(i*0.8).toFixed(3)));
    if(type==='step')   return Array.from({length:n},(_,i)=>i<n/2?0:1);
    if(type==='ramp')   return Array.from({length:n},(_,i)=>+(i/n).toFixed(3));
    if(type==='random') return Array.from({length:n},()=>+(randn()*0.6).toFixed(3));
    if(type==='bits')   return Array.from({length:n},()=>Math.random()>0.5?1:0);
    return genSeq('sin',n);
  };

  const runSequence = () => {
    setRunning(true);
    setTimeout(()=>{
      const xs = genSeq(inputSeq, seqLen);
      const steps=[]; let h=0, c=0;

      if(modelType==='lstm'){
        const sz=2; // concat [x, h_prev]
        const Wf=mkW(sz),Wi=mkW(sz),Wc=mkW(sz),Wo=mkW(sz);
        for(const x of xs){
          const r=lstmStep(x,h,c,Wf,Wi,Wc,Wo,randn()*0.1,randn()*0.1,randn()*0.1,randn()*0.1);
          steps.push({x,h:r.h,c:r.c,gates:r.gates});
          h=r.h; c=r.c;
        }
      } else if(modelType==='gru'){
        const sz=2;
        const Wr=mkW(sz),Wz=mkW(sz),Wn=mkW(sz);
        for(const x of xs){
          const r=gruStep(x,h,Wr,Wz,Wn,randn()*0.1,randn()*0.1,randn()*0.1);
          steps.push({x,h:r.h,gates:r.gates});
          h=r.h;
        }
      } else {
        const Wh=[randn()*0.5],Wx=[randn()*0.5];
        for(const x of xs){
          const r=rnnStep([x],h!==0?[h]:[0],Wh,Wx,randn()*0.1);
          steps.push({x,h:r.h,gates:r.gates});
          h=r.h;
        }
      }
      setResult({steps,xs,type:modelType});
      setSelectedT(0);
      log(`${modelType.toUpperCase()} seq=${seqLen} input=${inputSeq} | h_final=${steps.at(-1).h.toFixed(4)}`,"ok");
      setRunning(false);
    },20);
  };

  // Vanishing gradient simulation
  const vanishData = Array.from({length:50},(_,t)=>({
    t,
    rnn:   +(Math.pow(0.87,t)*Math.cos(t*0.3)+randn()*0.01).toFixed(4),
    lstm:  +(0.92*Math.sin(t*0.15+0.3)+randn()*0.01).toFixed(4),
    gru:   +(0.88*Math.sin(t*0.18+0.1)+randn()*0.01).toFixed(4),
  }));

  const gradData = Array.from({length:50},(_,t)=>({
    t,
    rnn:   +(Math.pow(0.5,t)*1.0).toFixed(6),
    lstm:  +(0.85*Math.exp(-t*0.015)+randn()*0.005).toFixed(6),
    gru:   +(0.82*Math.exp(-t*0.018)+randn()*0.005).toFixed(6),
  }));

  const sel = result?.steps[selectedT];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* Controls strip */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sel label="MODEL" value={modelType} onChange={setModelType} options={[{value:'lstm',label:'LSTM'},{value:'gru',label:'GRU'},{value:'rnn',label:'Vanilla RNN'}]}/>
        <Sel label="INPUT SEQUENCE" value={inputSeq} onChange={setInputSeq} options={['sin','step','ramp','random','bits'].map(v=>({value:v,label:v.toUpperCase()}))}/>
        <Sl label="Sequence Length" value={seqLen} min={4} max={20} onChange={setSeqLen} color="#a78bfa"/>
        <div style={{fontSize:'10px',color:'var(--t3)',padding:'8px',background:'var(--deep)',borderRadius:'3px',lineHeight:1.5}}>
          LSTM: 4 gates · GRU: 3 gates · RNN: 1 state
        </div>
        <Btn onClick={runSequence} disabled={running} color="#a78bfa">{running?'RUNNING…':'RUN →'}</Btn>
      </div>

      {/* ── UNROLLED TAB ── */}
      {tab==='unrolled'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title={`${modelType.toUpperCase()} UNROLLED — ${seqLen} TIMESTEPS · DOT SIZE = |h(t)|`} accent={AC}>
            {result
              ? <UnrolledRNN steps={result.xs} hiddenVals={result.steps.map(s=>s.h)} type={result.type} gates={result.steps.map(s=>s.gates)}/>
              : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b'}}>Hit RUN to unroll the {modelType.toUpperCase()} across time</div>
            }
          </Panel>

          {result&&(
            <>
              {/* Step selector */}
              <div style={{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
                <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>INSPECT TIMESTEP:</span>
                {result.steps.map((_,t)=>(
                  <button key={t} onClick={()=>setSelectedT(t)} style={{padding:'4px 10px',border:`1px solid ${selectedT===t?'#fbbf24':'var(--b0)'}`,background:selectedT===t?'#fbbf2415':'transparent',color:selectedT===t?'#fbbf24':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>t={t}</button>
                ))}
              </div>

              {/* Step details */}
              {sel&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <Panel title={`TIMESTEP t=${selectedT} — INPUT x=${sel.x}`} accent="#fbbf24">
                    <div style={{marginBottom:'10px'}}>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.08em'}}>GATE ACTIVATIONS</div>
                      {sel.gates&&Object.entries(sel.gates).map(([g,v])=>(
                        <GateBar key={g} val={Math.max(0,Math.min(1,typeof v==='number'?Math.abs(v):0))} label={g==='f'?'Forget f':g==='i'?'Input i':g==='o'?'Output o':g==='r'?'Reset r':g==='z'?'Update z':g==='c_tilde'?'Cell c̃':'h'}
                          color={g==='f'?'#f87171':g==='i'?'#22d3ee':g==='o'?'#4ade80':g==='r'?'#fb923c':g==='z'?'#a78bfa':'#fbbf24'}/>
                      ))}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',marginTop:'8px'}}>
                      <Metric label="h(t)" value={sel.h.toFixed(4)} color="#a78bfa"/>
                      {sel.c!==undefined&&<Metric label="c(t)" value={sel.c.toFixed(4)} color="#22d3ee"/>}
                    </div>
                  </Panel>

                  <Panel title="HIDDEN STATE OVER TIME" accent="#a78bfa">
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={result.steps.map((s,t)=>({t,h:+s.h.toFixed(4),x:+s.x.toFixed(4)}))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[-1.1,1.1]}/>
                        <Tooltip/>
                        <ReferenceLine x={selectedT} stroke="#fbbf24" strokeWidth={2}/>
                        <Line type="monotone" dataKey="x" stroke="#64748b" dot={{r:2}} strokeDasharray="3 2" name="input x(t)"/>
                        <Line type="monotone" dataKey="h" stroke="#a78bfa" strokeWidth={2} dot={{r:3}} name="hidden h(t)"/>
                        <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── GATES TAB ── */}
      {tab==='gates'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {result
            ? <>
                <Panel title={`ALL GATE ACTIVATIONS — ${result.type.toUpperCase()} — ACROSS SEQUENCE`} accent={AC}>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={result.steps.map((s,t)=>({t,h:+s.h.toFixed(4),...Object.fromEntries(Object.entries(s.gates||{}).map(([k,v])=>[k,+Math.abs(v).toFixed(4)]))}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'timestep',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1.1]}/>
                      <Tooltip/>
                      <Legend/>
                      {result.type==='lstm'&&<>
                        <Line type="monotone" dataKey="f" stroke="#f87171" strokeWidth={2} dot={{r:3}} name="Forget gate"/>
                        <Line type="monotone" dataKey="i" stroke="#22d3ee" strokeWidth={2} dot={{r:3}} name="Input gate"/>
                        <Line type="monotone" dataKey="o" stroke="#4ade80" strokeWidth={2} dot={{r:3}} name="Output gate"/>
                      </>}
                      {result.type==='gru'&&<>
                        <Line type="monotone" dataKey="r" stroke="#fb923c" strokeWidth={2} dot={{r:3}} name="Reset gate"/>
                        <Line type="monotone" dataKey="z" stroke="#a78bfa" strokeWidth={2} dot={{r:3}} name="Update gate"/>
                      </>}
                      <Line type="monotone" dataKey="h" stroke="#fbbf24" strokeWidth={2} strokeDasharray="5 3" name="h(t)"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>

                {/* Gate heatmap */}
                {result.type==='lstm'&&(
                  <Panel title="GATE HEATMAP — EACH ROW = ONE GATE, EACH COL = ONE TIMESTEP" accent="#fbbf24">
                    <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                      {[
                        {key:'f',label:'Forget f',color:'#f87171'},
                        {key:'i',label:'Input i',color:'#22d3ee'},
                        {key:'o',label:'Output o',color:'#4ade80'},
                      ].map(({key,label,color})=>(
                        <div key={key} style={{display:'flex',alignItems:'center',gap:'8px'}}>
                          <span style={{fontSize:'9px',color,minWidth:60,fontFamily:'JetBrains Mono',fontWeight:700}}>{label}</span>
                          <div style={{display:'flex',gap:'2px',flex:1}}>
                            {result.steps.map((s,t)=>{
                              const v=Math.abs(s.gates[key]??0);
                              return <div key={t} title={`t=${t}: ${(s.gates[key]??0).toFixed(3)}`}
                                style={{flex:1,height:24,background:`rgba(${color==='#f87171'?'248,113,113':color==='#22d3ee'?'34,211,238':'74,222,128'},${0.05+v*0.95})`,borderRadius:'2px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'8px',color:'#000',fontFamily:'JetBrains Mono'}}>
                                {seqLen<=12?v.toFixed(1):''}
                              </div>;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Panel>
                )}
              </>
            : <div style={{padding:'60px',textAlign:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run the model first</div>
          }
          <LogPanel logs={logs} height={90}/>
        </div>
      )}

      {/* ── CELL STATE TAB ── */}
      {tab==='cell-state'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {result&&result.type==='lstm'
            ? <>
                <Panel title="LSTM CELL STATE c(t) vs HIDDEN STATE h(t)" accent="#22d3ee">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Cell state = long-term memory highway. Hidden = short-term working memory.</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={result.steps.map((s,t)=>({t,c:+s.c.toFixed(4),h:+s.h.toFixed(4),f:+s.gates.f.toFixed(4),i:+s.gates.i.toFixed(4)}))}>
                      <defs>
                        <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="hGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#a78bfa" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Area type="monotone" dataKey="c" stroke="#22d3ee" fill="url(#cGrad)" strokeWidth={2} name="cell c(t)"/>
                      <Area type="monotone" dataKey="h" stroke="#a78bfa" fill="url(#hGrad)" strokeWidth={2} name="hidden h(t)"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </Panel>

                {/* Cell state update diagram */}
                <Panel title="FORGET vs INPUT GATE — WHAT GETS KEPT vs WRITTEN" accent="#fbbf24">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>c(t) = f(t) × c(t-1) + i(t) × c̃(t).  Forget gate erases. Input gate writes new info.</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={result.steps.map((s,t)=>({t,forget:+s.gates.f.toFixed(3),input:+s.gates.i.toFixed(3)}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <Tooltip/>
                      <Legend/>
                      <Bar dataKey="forget" fill="#f87171" opacity={0.85} name="Forget gate" radius={[2,2,0,0]}/>
                      <Bar dataKey="input" fill="#22d3ee" opacity={0.85} name="Input gate" radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </>
            : result&&result.type==='gru'
              ? <Panel title="GRU: UPDATE GATE z(t)" accent="#a78bfa">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>h(t) = (1-z) × n(t) + z × h(t-1). Update gate z controls how much old state to keep.</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={result.steps.map((s,t)=>({t,h:+s.h.toFixed(4),z:+s.gates.z.toFixed(4),r:+s.gates.r.toFixed(4)}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="h" stroke="#a78bfa" strokeWidth={2} dot={{r:3}} name="h(t)"/>
                      <Line type="monotone" dataKey="z" stroke="#fbbf24" strokeWidth={2} dot={{r:3}} name="Update z"/>
                      <Line type="monotone" dataKey="r" stroke="#fb923c" strokeWidth={2} dot={{r:3}} name="Reset r"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              : <div style={{padding:'60px',textAlign:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run LSTM to see cell state</div>
          }
        </div>
      )}

      {/* ── VANISHING TAB ── */}
      {tab==='vanishing'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="GRADIENT MAGNITUDE OVER TIMESTEPS — BACKPROP THROUGH TIME" accent="#f87171">
            <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Vanilla RNN gradient shrinks exponentially. LSTM/GRU maintain highways via gating.</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={gradData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'timesteps back',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={[0.001,2]}/>
                <Tooltip/>
                <Legend/>
                <Line type="monotone" dataKey="rnn" stroke="#f87171" strokeWidth={2} dot={false} name="Vanilla RNN (vanishes)"/>
                <Line type="monotone" dataKey="lstm" stroke="#22d3ee" strokeWidth={2} dot={false} name="LSTM (stable)"/>
                <Line type="monotone" dataKey="gru" stroke="#4ade80" strokeWidth={2} dot={false} strokeDasharray="5 3" name="GRU (stable, simpler)"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="MEMORY RETENTION — HOW WELL DOES PAST INFO SURVIVE?" accent="#a78bfa">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={vanishData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[-1.1,1.1]}/>
                <Tooltip/>
                <Legend/>
                <Line type="monotone" dataKey="rnn" stroke="#f87171" strokeWidth={2} dot={false} name="RNN"/>
                <Line type="monotone" dataKey="lstm" stroke="#22d3ee" strokeWidth={2} dot={false} name="LSTM"/>
                <Line type="monotone" dataKey="gru" stroke="#4ade80" strokeWidth={2} dot={false} name="GRU"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
            {[
              {name:'Vanilla RNN',color:'#f87171',eq:'h(t) = tanh(Wh·h+Wx·x)',mem:'Short only',params:'~2·(h²+hx)',gates:0,prob:'Vanishing gradient beyond ~10 steps'},
              {name:'LSTM',color:'#22d3ee',eq:'c(t) = f⊙c + i⊙c̃',mem:'Long + short',params:'~4·(h²+hx+h)',gates:4,prob:'4× more params than RNN'},
              {name:'GRU',color:'#4ade80',eq:'h(t) = (1-z)·n + z·h',mem:'Long + short',params:'~3·(h²+hx+h)',gates:3,prob:'Simpler, often matches LSTM'},
            ].map(m=>(
              <Panel key={m.name} title={m.name} accent={m.color}>
                <div style={{fontSize:'10px',color:'var(--t2)',fontFamily:'JetBrains Mono',marginBottom:'8px',padding:'6px',background:'var(--deep)',borderRadius:'3px'}}>{m.eq}</div>
                {[['Memory',m.mem,m.color],['Gates',m.gates,m.color],['Params',m.params,'var(--t2)'],['Weakness',m.prob,'#64748b']].map(([k,v,c])=>(
                  <div key={k} style={{marginBottom:'4px',fontSize:'9px',display:'flex',gap:'6px'}}>
                    <span style={{color:'var(--t3)',minWidth:60}}>{k}</span>
                    <span style={{color:c}}>{v}</span>
                  </div>
                ))}
              </Panel>
            ))}
          </div>
        </div>
      )}

      {/* ── COMPARE TAB ── */}
      {tab==='compare'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
            {['lstm','gru','rnn'].map(type=>{
              const xs=genSeq('sin',12); let h=0,c=0;
              const steps=[];
              if(type==='lstm'){
                const sz=2,Wf=mkW(sz),Wi=mkW(sz),Wc=mkW(sz),Wo=mkW(sz);
                xs.forEach(x=>{const r=lstmStep(x,h,c,Wf,Wi,Wc,Wo,0,0,0,0);steps.push({x,h:r.h,c:r.c});h=r.h;c=r.c;});
              } else if(type==='gru'){
                const sz=2,Wr=mkW(sz),Wz=mkW(sz),Wn=mkW(sz);
                xs.forEach(x=>{const r=gruStep(x,h,Wr,Wz,Wn,0,0,0);steps.push({x,h:r.h});h=r.h;});
              } else {
                const Wh=[randn()*0.5],Wx=[randn()*0.5];
                xs.forEach(x=>{const r=rnnStep([x],h?[h]:[0],Wh,Wx,0);steps.push({x,h:r.h});h=r.h;});
              }
              const color=type==='lstm'?'#22d3ee':type==='gru'?'#4ade80':'#f87171';
              return (
                <Panel key={type} title={`${type.toUpperCase()} — UNROLLED`} accent={color}>
                  <UnrolledRNN steps={xs.slice(0,8)} hiddenVals={steps.slice(0,8).map(s=>s.h)} type={type}/>
                  <div style={{marginTop:'8px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px'}}>
                    <Metric label="h_final" value={steps.at(-1)?.h.toFixed(4)} color={color}/>
                    <Metric label="|h| range" value={`${Math.min(...steps.map(s=>Math.abs(s.h))).toFixed(2)}–${Math.max(...steps.map(s=>Math.abs(s.h))).toFixed(2)}`} color={color}/>
                  </div>
                </Panel>
              );
            })}
          </div>
          <LogPanel logs={logs} height={90}/>
        </div>
      )}
    </div>
  );
}
